import { useState } from "react";
import { toast } from "sonner";
import { http, formatApiError } from "../lib/api";

const EMPTY = { author: "", email: "", content: "" };

export function CommentForm({ slug, onPosted }) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const field = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const postComment = async (e) => {
    e.preventDefault();
    if (!form.author.trim() || !form.content.trim()) return;
    setSubmitting(true);
    try {
      const { data: c } = await http.post(`/posts/${slug}/comments`, form);
      onPosted(c);
      setForm(EMPTY);
      toast.success("Comment posted.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={postComment} className="space-y-6" data-testid="comment-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <input type="text" required placeholder="Your name" value={form.author} onChange={field("author")} className="ghost-input" data-testid="comment-author-input" />
        <input type="email" placeholder="Email (optional)" value={form.email} onChange={field("email")} className="ghost-input" data-testid="comment-email-input" />
      </div>
      <textarea
        required
        placeholder="Share your thoughts…"
        value={form.content}
        onChange={field("content")}
        rows={4}
        className="w-full bg-transparent border-b border-[#151e1a]/40 focus:border-[#1e4a35] outline-none py-3 resize-none"
        data-testid="comment-content-input"
      />
      <button type="submit" className="btn-primary" disabled={submitting} data-testid="comment-submit-button">
        {submitting ? "Posting…" : "Post comment"}
      </button>
    </form>
  );
}

export function CommentList({ comments }) {
  if (comments.length === 0) {
    return <p className="text-[#66726d] italic" data-testid="comments-empty">No comments yet. Be the first.</p>;
  }
  return comments.map((c) => (
    <div key={c.id} className="border-l-2 pl-5" style={{ borderColor: "var(--accent)" }} data-testid={`comment-${c.id}`}>
      <p className="font-medium text-[#151e1a]">{c.author}</p>
      <p className="font-mono text-[10px] uppercase tracking-widest text-[#66726d]">
        {new Date(c.created_at).toLocaleDateString("en-AU", { month: "long", day: "numeric", year: "numeric" })}
      </p>
      <p className="mt-3 text-[#404e48] font-light leading-relaxed whitespace-pre-line">{c.content}</p>
    </div>
  ));
}

export default function CommentsSection({ slug, comments, onPosted }) {
  return (
    <section className="border-t py-10 md:py-14 px-6 md:px-12" style={{ borderColor: "var(--border)", backgroundColor: "var(--bg-alt)" }}>
      <div className="max-w-2xl mx-auto">
        <p className="overline mb-3">Response</p>
        <h2 className="font-serif text-3xl md:text-4xl font-light tracking-tight mb-8">Leave a note</h2>
        <CommentForm slug={slug} onPosted={onPosted} />
        <div className="mt-14 space-y-8">
          <CommentList comments={comments} />
        </div>
      </div>
    </section>
  );
}
